import { UserAccessRightService } from 'src/app/shared/services/user-access-rights.service'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { combineLatest, Observable, of } from 'rxjs'
import { catchError, filter, map, take, timeout } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { RoleBoundTile } from 'src/app/shared/interfaces'
import { environment } from 'src/environments/environment'
import { v4 as uuidv4 } from 'uuid'
import { Component, Input, OnInit } from '@angular/core'
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { courseSelectors } from '../../course/course.selectors'
import { Document, DocumentType } from '../../document/document'
import { documentActions } from '../../document/document.actions'
import { productSelectors } from '../../product/product.selectors'
import { RolePermission } from '../../role/role-permission/role-permission'
import { rolePermissionActions } from '../../role/role-permission/role-permission.actions'
import { roleSelectors } from '../../role/role.selectors'
import { configurationOptionSelectors } from '../configuration-option.selectors'
import { configurationOptionActions } from '../configuration-option.actions'
import { ConfigurationOption } from '../configuration-option'
import { rolePermissionSelectors } from '../../role/role-permission/role-permission.selectors'
import { ConfigOptionsKeys } from 'src/app/shared/constants'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { CdkDragDrop } from '@angular/cdk/drag-drop'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { TranslateService } from '@ngx-translate/core'
@UntilDestroy()
@Component({
  selector: 'configuration-option-organization-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class ConfigurationOptionShopTilesTabComponent implements OnInit {
  @Input() onClickCreateTile$: Observable<boolean>
  public form: UntypedFormGroup
  public isLoading$: Observable<boolean>
  public configOption$: Observable<ConfigurationOption>
  public isCollapsed = []
  public accessRights = AccessRights
  public roleBoundTiles$: Observable<RoleBoundTile[]>
  public disableSaveButton: boolean = true
  public rolePermissions$: Observable<RolePermission[]>
  public languageSelected$: Observable<string>

  public availableLanguages = environment.frontendLanguages.reduce(
    (acc, curr) => {
      if (curr != 'xx') return ((acc[curr] = true), acc)
      else return acc
    },
    {}
  )
  public config: any = {
    placeholder: '',
    tabsize: 2,
    dialogsInBody: true,
    height: '200px',
    toolbar: [
      ['misc', ['codeview', 'undo', 'redo']],
      [
        'font',
        [
          'bold',
          'italic',
          'underline',
          'strikethrough',
          'superscript',
          'subscript',
          'clear'
        ]
      ],
      ['fontsize', ['fontname', 'fontsize', 'color']],
      ['para', ['style', 'ul', 'ol', 'paragraph', 'height']],
      ['insert', ['table', 'picture', 'link', 'video', 'hr']]
    ],
    fontNames: [
      'Helvetica',
      'Arial',
      'Arial Black',
      'Comic Sans MS',
      'Courier New',
      'Roboto',
      'Times',
      'opensans',
      'sansserif',
      'monospace',
      'serif'
    ]
  }
  public hideTileContent: boolean[] = []
  public tiles: any[] = []

  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private notificationService: NotificationService,
    private actions$: Actions,
    private translate: TranslateService,
    public accessRightService: UserAccessRightService
  ) {}

  get tileFormArray() {
    return this.form?.controls?.tiles as UntypedFormArray
  }

  ngOnInit() {
    this.onClickCreateTile$.pipe(untilDestroyed(this)).subscribe((clicked) => {
      if (clicked) {
        this.disableSaveButton = false
        this.addTileRow(clicked)
      }
    })
    this.languageSelected$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )
    this.isLoading$ = combineLatest([
      this.store.select(productSelectors.selectIsLoading),
      this.store.select(courseSelectors.selectIsLoading),
      this.store.select(roleSelectors.selectIsLoading)
    ]).pipe(map(([a, b, c]) => a || b || c))

    this.store.dispatch(
      rolePermissionActions.fetchMany({
        params: RequestQueryBuilder.create({
          join: [{ field: 'role' }, { field: 'product' }]
        })
      })
    )

    this.rolePermissions$ = combineLatest([
      this.store.select(rolePermissionSelectors.selectAll),
      this.store.select(productSelectors.selectEntities),
      this.store.select(roleSelectors.selectEntities)
    ]).pipe(
      map(([rolePermissions, productEntities, roleEntities]) => {
        if (
          Object.keys(productEntities).length == 0 ||
          Object.keys(roleEntities).length == 0
        ) {
          return []
        }
        return rolePermissions.map((rolePermission, index) => {
          const description = `Permission #${rolePermission.id} - ${
            roleEntities[rolePermission.roleId]?.description
          } - ${productEntities[rolePermission.productId]?.description}`
          return {
            ...rolePermission,
            description
          }
        })
      })
    )

    this.store.dispatch(
      configurationOptionActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            key: ConfigOptionsKeys.ROLE_BOUND_TILES
          }
        })
      })
    )
    this.configOption$ = this.store.select(
      configurationOptionSelectors.selectByKey,
      'role_bound_tiles'
    )

    this.roleBoundTiles$ = this.configOption$.pipe(
      filter((x) => Boolean(x)),
      map((configOption) => JSON.parse(configOption.value))
    )

    this.form = this.fb.group({
      tiles: this.fb.array([])
    })

    this.roleBoundTiles$.pipe(take(1)).subscribe((tiles) => {
      for (let tile of tiles) {
        this.addTileRow()
      }
      this.tiles = tiles.sort((a, b) => (a['order'] < b['order'] ? -1 : 1))
      this.form?.controls.tiles.patchValue(tiles)
    })
  }

  public getTileRow() {
    const descriptionsAndSubTitles = {}
    Object.keys(this.availableLanguages).map((language) => {
      descriptionsAndSubTitles[`description_${language}`] = [
        '',
        Validators.required
      ]
      descriptionsAndSubTitles[`subtitle_${language}`] = [
        '',
        Validators.required
      ]
    })
    return this.fb.group({
      isActive: [false, []],
      order: [this.tiles.length, [Validators.required]],

      imagePath: ['', [Validators.required]],
      permissionIds: [null, []],
      ...descriptionsAndSubTitles
    })
  }
  public onClickEdit(event: any, index: number) {
    this.isCollapsed[index] = !this.isCollapsed[index]
    this.disableSaveButton = false
  }
  public addTileRow(createNewOne?: any) {
    this.tileFormArray.push(this.getTileRow())
    this.tiles.push(this.getTileRow())

    this.isCollapsed.push(createNewOne)
  }

  public onSubmit() {
    if (!this.form.valid) {
      this.notificationService.emit('Please fill out every field', 'warning')

      return
    }

    const configOptionValue = JSON.stringify(this.form.value?.tiles)

    this.configOption$.pipe(take(1)).subscribe((configOption) => {
      if (!configOption) {
        this.store.dispatch(
          configurationOptionActions.createOne({
            entity: {
              key: 'role_bound_tiles',
              value: configOptionValue
            }
          })
        )
      } else {
        this.store.dispatch(
          configurationOptionActions.updateOneById({
            id: configOption.id,
            params: {
              value: configOptionValue
            }
          })
        )
      }
    })

    this.actions$
      .pipe(
        ofType(configurationOptionActions.upsertMany),
        take(1),
        timeout(1000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        action
          ? this.notificationService.emit('alerts.success', 'success')
          : null
        this.disableSaveButton = true
      })
  }

  public getTiles(form) {
    return form.controls.tiles.controls
  }

  public getImageUrl(imagePath: string) {
    return `${environment.staticAssetBaseUrl}${imagePath}`
  }

  public onRemoveClick(event: any, index: number) {
    const message = this.translate.instant('alerts.confirmShopDelete')
    if (!window.confirm(message)) {
      return
    }
    event.stopPropagation()
    this.tileFormArray.removeAt(index)
    this.tiles = this.tiles.filter((e, i) => i !== index)

    this.isCollapsed.splice(index, 1)
    this.tileFormArray.controls.forEach((tile, tileIndex) => {
      tile.get('order').setValue(tileIndex)
    })
    this.onSubmit()
  }

  public onSubmitImage(file: File, index: number) {
    const formGroup = this.tileFormArray.controls[index] as UntypedFormGroup

    this.store.dispatch(
      documentActions.createOneWithFile({
        entity: {
          type: DocumentType.BRANDING_IMAGE,
          isPublic: true,
          path: `branding/${uuidv4()}`
        },
        file
      })
    )

    this.actions$
      .pipe(
        ofType(documentActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (!action) {
          return this.notificationService.emit('alerts.warning', 'error')
        }
        const document: Document = action.entities[0]
        formGroup.controls.imagePath.patchValue(document.path)
        this.notificationService.emit('alerts.success', 'success')
      })
  }

  public drop(event: CdkDragDrop<any[]>, tile) {
    const [movedTile] = this.tiles.splice(event.previousIndex, 1)
    this.tiles.splice(event.currentIndex, 0, movedTile)

    const movedControl = this.tileFormArray.at(event.previousIndex)
    this.tileFormArray.removeAt(event.previousIndex)
    this.tileFormArray.insert(event.currentIndex, movedControl)
    this.tileFormArray.controls.forEach((tileControl, index) => {
      tileControl.get('order').setValue(index)
    })

    this.onSubmit()
  }
  public toggle(index) {
    if (!this.hideTileContent[index]) {
      this.hideTileContent[index] = true
    } else {
      this.hideTileContent[index] = !this.hideTileContent[index]
    }
  }
  public isAllCollapsed(arrayTilesCollapseds: any[]) {
    const lengthArray = arrayTilesCollapseds.length
    return lengthArray == arrayTilesCollapseds.filter((value) => !value).length
  }
}
